import { DEFAULT_PARAMS } from '@constants';
import { IParams } from 'interfaces';
import { GENDER, IExtraDataPromocode } from 'types';
import { IReactionDisposer, makeAutoObservable, reaction } from 'mobx';
import { ISurvey } from '@verv-inc/js-questionnaire/dist/esm/models/survey/ISurvey';
import { IABTestsStore } from './abTestsStore';
import { INavigationStore as IPaymentNavigationStore } from 'modules/payment/store/navigationStore';
import { IActivationStore } from 'modules/activationInstructions/store';
import { IPaymentStore } from 'modules/payment/store';
import { IPaywallStore } from 'modules/paywall/store';
import { withDefaultParams, getPersistedValue, persistValue, updateLocationSearch } from '@helpers';
import { isUAEntryPointRoute } from 'router';
import { IUnitsStore } from './unitsStore';
import { IVervAPIServices } from 'utils/services/VervAPI';
import { IAnalyticsTargetedEventsTracker, IErrorLoggerWithUserSupport } from '@verv-inc/js-toolkit';
import 'core-js/features/object/from-entries';

enum STORAGE_KEYS {
	extraDataPromocode = 'extraDataPromocode',
	sourceLocation = 'sourceLocation',
	emailInApplication = 'emailInApplication',
}

export interface IAppStore {
	params: IParams;
	sourceLocation: Location;
	sourceSearchParams: URLSearchParams;
	extraDataPromocode: IExtraDataPromocode | undefined;
	emailInApplication: string | undefined;

	unitsStore: IUnitsStore;
	surveyStore: ISurvey;
	abTestsStore: IABTestsStore;
	paymentNavigationStore: IPaymentNavigationStore;
	activationInstructionsStore: IActivationStore;
	paymentStore: IPaymentStore;
	paywallStore: IPaywallStore;

	VervAPI: IVervAPIServices;
	eventsTracker: IAnalyticsTargetedEventsTracker;
	errorLogger: IErrorLoggerWithUserSupport;

	setSourceSearchParams: (params: URLSearchParams) => void;
	setExtraDataPromocode: (promocode: IExtraDataPromocode) => void;
	setEmailInApplication: (email: string | undefined) => void;
}

interface AppStoreDependencies {
	location: Location;
	storage: Storage;
	VervAPI: IVervAPIServices;
	eventsTracker: IAnalyticsTargetedEventsTracker;
	errorLogger: IErrorLoggerWithUserSupport;
	unitsStoreCreator: () => IUnitsStore;
	surveyStoreCreator: () => ISurvey;
	abTestsStoreCreator: (searchParams: URLSearchParams) => IABTestsStore;
	paymentNavigationStoreCreator: () => IPaymentNavigationStore;
	activationInstructionsStoreCreator: (searchParams: URLSearchParams) => IActivationStore;
	paymentStoreCreator: (searchParams: URLSearchParams) => IPaymentStore;
	paywallStoreCreator: (searchParams: URLSearchParams) => IPaywallStore;
}

export class AppStore implements IAppStore {
	params: IParams = DEFAULT_PARAMS;
	sourceLocation: Location;
	sourceSearchParams: URLSearchParams;
	extraDataPromocode: IExtraDataPromocode | undefined;
	emailInApplication: string | undefined;

	unitsStore: IUnitsStore;
	surveyStore: ISurvey;
	abTestsStore: IABTestsStore;
	paymentNavigationStore: IPaymentNavigationStore;
	activationInstructionsStore: IActivationStore;
	paymentStore: IPaymentStore;
	paywallStore: IPaywallStore;

	VervAPI: IVervAPIServices;
	eventsTracker: IAnalyticsTargetedEventsTracker;
	errorLogger: IErrorLoggerWithUserSupport;

	private readonly storage: Storage;
	private readonly surveyStoreAnswersDisposer: IReactionDisposer;

	constructor(dependencies: AppStoreDependencies) {
		this.storage = dependencies.storage;
		this.VervAPI = dependencies.VervAPI;
		this.eventsTracker = dependencies.eventsTracker;
		this.errorLogger = dependencies.errorLogger;

		if (isUAEntryPointRoute(location.pathname)) {
			const AB_TEST_KEY = 'onboarding_newLink_ab_test';
			const abValue = localStorage.getItem(AB_TEST_KEY);
			const emailValue = localStorage.getItem('emailInApplication');
			dependencies.storage.clear();
			console.log('storage cleared');
			if (abValue) {
				localStorage.setItem(AB_TEST_KEY, abValue);
			}
			if (emailValue) {
				localStorage.setItem('emailInApplication', emailValue);
			}
			const updatedLocation = updateLocationSearch(location);
			persistValue(updatedLocation, STORAGE_KEYS.sourceLocation, dependencies.storage);
		}
		this.sourceLocation =
			getPersistedValue(STORAGE_KEYS.sourceLocation, dependencies.storage) ?? location;
		this.sourceSearchParams = new URLSearchParams(this.sourceLocation.search);
		this.extraDataPromocode = getPersistedValue(
			STORAGE_KEYS.extraDataPromocode,
			dependencies.storage,
		);
		this.emailInApplication = getPersistedValue(
			STORAGE_KEYS.emailInApplication,
			dependencies.storage,
		);

		this.unitsStore = dependencies.unitsStoreCreator();
		this.surveyStore = dependencies.surveyStoreCreator();
		this.paymentNavigationStore = dependencies.paymentNavigationStoreCreator();
		this.activationInstructionsStore = dependencies.activationInstructionsStoreCreator(
			this.sourceSearchParams,
		);

		this.abTestsStore = dependencies.abTestsStoreCreator(this.sourceSearchParams);
		this.paymentStore = dependencies.paymentStoreCreator(this.sourceSearchParams);
		this.paywallStore = dependencies.paywallStoreCreator(this.sourceSearchParams);

		makeAutoObservable(this);

		// Observe gender in survey answers, and override global gender value.
		this.surveyStoreAnswersDisposer = reaction(
			() =>
				this.surveyStore.answers.gender ||
				this.surveyStore.answers.three_genders ||
				this.surveyStore.answers.gender_custom,
			value => {
				if (value) {
					let newGender;
					if (value.join() === 'intersex') {
						newGender = 'female' as GENDER;
					} else {
						newGender = value.join() as GENDER;
					}

					if (this.params.gender !== newGender) {
						this.params.gender = newGender;
					}
				}
			},
		);
	}

	setSourceSearchParams = (params: URLSearchParams): void => {
		this.sourceSearchParams = params;

		this.abTestsStore.setSearchParams(this.sourceSearchParams);
		this.paymentStore.setSearchParams(this.sourceSearchParams);
		this.paywallStore.setSearchParams(this.sourceSearchParams);

		const sourceParameters = Object.fromEntries(params.entries());
		this.params = {
			...withDefaultParams(sourceParameters),
		};
	};

	setExtraDataPromocode = (promocode: IExtraDataPromocode) => {
		this.extraDataPromocode = promocode;
		persistValue(promocode, STORAGE_KEYS.extraDataPromocode, this.storage);
	};

	setEmailInApplication = (email: string | undefined) => {
		this.emailInApplication = email;
		persistValue(email, STORAGE_KEYS.emailInApplication, this.storage);
	};

	dispose() {
		// So, to avoid subtle memory issues, always call the
		// disposers when the reactions are no longer needed.
		this.surveyStoreAnswersDisposer();
	}
}
